import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffAPI } from '../utils/api';

const T = {
  vi: {
    title: 'Cổng Nhân Viên',
    subtitle: 'Benz Eatery',
    username: 'Tên đăng nhập',
    password: 'Mật khẩu',
    usernamePh: 'Nhập tên đăng nhập',
    passwordPh: 'Nhập mật khẩu',
    signIn: 'Đăng nhập',
    signingIn: 'Đang đăng nhập...',
    installApp: 'Cài đặt ứng dụng',
    installDesc: 'Thêm vào màn hình chính để truy cập nhanh',
    iosInstall: 'Cài trên iPhone/iPad',
    iosStep1: 'Nhấn nút Chia sẻ',
    iosStep2: 'Chọn "Thêm vào MH chính"',
    androidInstall: 'Cài đặt ngay',
    dismiss: 'Để sau',
  },
  en: {
    title: 'Staff Portal',
    subtitle: 'Benz Eatery',
    username: 'Username',
    password: 'Password',
    usernamePh: 'Enter username',
    passwordPh: 'Enter password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    installApp: 'Install App',
    installDesc: 'Add to home screen for quick access',
    iosInstall: 'Install on iPhone/iPad',
    iosStep1: 'Tap the Share button',
    iosStep2: 'Select "Add to Home Screen"',
    androidInstall: 'Install Now',
    dismiss: 'Later',
  },
};

const StaffLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('vi');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const navigate = useNavigate();
  const t = T[lang];

  // Detect platform and install state
  useEffect(() => {
    // Swap manifest for Staff PWA
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.setAttribute('href', '/staff-manifest.json');

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsIOS(ios);
    setIsStandalone(standalone);

    // Show install prompt if not already installed
    if (!standalone && !localStorage.getItem('staff_install_dismissed')) {
      setShowInstall(true);
    }

    // Android: capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!standalone) setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstall(false);
    localStorage.setItem('staff_install_dismissed', '1');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await staffAPI.login({ username: username.trim(), password });
      localStorage.setItem('staff_token', res.data.token);
      localStorage.setItem('staff_user', JSON.stringify(res.data.staff));
      navigate('/staff/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="sl-root" data-testid="staff-login-page">
      {/* Install Banner */}
      {showInstall && !isStandalone && (
        <div className="sl-install-banner" data-testid="install-banner">
          <div className="sl-install-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </div>
          <div className="sl-install-text">
            <strong>{t.installApp}</strong>
            <span>{t.installDesc}</span>
          </div>
          {isIOS ? (
            <div className="sl-install-ios">
              <p>{t.iosStep1} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display:'inline',verticalAlign:'middle'}}><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></p>
              <p>{t.iosStep2}</p>
            </div>
          ) : deferredPrompt ? (
            <button onClick={installAndroid} className="sl-install-btn" data-testid="install-btn">
              {t.androidInstall}
            </button>
          ) : (
            <div className="sl-install-ios">
              <p>{t.iosStep1} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display:'inline',verticalAlign:'middle'}}><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg></p>
              <p>{t.iosStep2}</p>
            </div>
          )}
          <button onClick={dismissInstall} className="sl-install-dismiss" data-testid="install-dismiss">{t.dismiss}</button>
        </div>
      )}

      <div className="sl-card">
        <div className="sl-lang-toggle">
          <button onClick={() => setLang('vi')} className={`sl-lang-btn ${lang === 'vi' ? 'active' : ''}`}>VI</button>
          <button onClick={() => setLang('en')} className={`sl-lang-btn ${lang === 'en' ? 'active' : ''}`}>EN</button>
        </div>
        <div className="sl-logo">
          <div className="sl-logo-mark">B</div>
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
        <form onSubmit={handleLogin} className="sl-form">
          {error && <div className="sl-error" data-testid="staff-login-error">{error}</div>}
          <div className="sl-field">
            <label>{t.username}</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder={t.usernamePh}
              autoComplete="username"
              required
              data-testid="staff-username-input"
            />
          </div>
          <div className="sl-field">
            <label>{t.password}</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t.passwordPh}
              autoComplete="current-password"
              required
              data-testid="staff-password-input"
            />
          </div>
          <button type="submit" className="sl-btn" disabled={loading} data-testid="staff-login-btn">
            {loading ? t.signingIn : t.signIn}
          </button>
        </form>
      </div>
      <style>{`
        .sl-root {
          min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: #0f172a; padding: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          gap: 16px;
        }
        .sl-card {
          background: #1e293b; border-radius: 24px; padding: 40px 28px;
          width: 100%; max-width: 380px; border: 1px solid #334155; position: relative;
        }
        .sl-lang-toggle {
          position: absolute; top: 16px; right: 16px; display: flex; gap: 4px;
        }
        .sl-lang-btn {
          padding: 4px 10px; border-radius: 8px; border: 1px solid #334155;
          background: transparent; color: #64748b; font-size: 11px; font-weight: 700;
          cursor: pointer; transition: all 0.15s;
        }
        .sl-lang-btn.active { background: #f59e0b; color: #0f172a; border-color: #f59e0b; }
        .sl-logo { text-align: center; margin-bottom: 32px; }
        .sl-logo-mark {
          width: 56px; height: 56px; background: linear-gradient(135deg, #f59e0b, #ea580c);
          border-radius: 16px; display: flex; align-items: center; justify-content: center;
          font-size: 28px; font-weight: 800; color: white; margin: 0 auto 12px;
          font-family: Georgia, serif;
        }
        .sl-logo h1 { color: #f8fafc; font-size: 22px; font-weight: 700; margin: 0; }
        .sl-logo p { color: #64748b; font-size: 13px; margin-top: 4px; }
        .sl-form { display: flex; flex-direction: column; gap: 16px; }
        .sl-field label {
          display: block; color: #94a3b8; font-size: 12px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;
        }
        .sl-field input {
          width: 100%; padding: 14px 16px; background: #0f172a; border: 1px solid #334155;
          border-radius: 14px; color: #f8fafc; font-size: 16px; outline: none;
          font-family: inherit; box-sizing: border-box;
        }
        .sl-field input:focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
        .sl-field input::placeholder { color: #475569; }
        .sl-btn {
          width: 100%; padding: 14px; background: linear-gradient(135deg, #f59e0b, #ea580c);
          border: none; border-radius: 14px; color: white; font-size: 16px;
          font-weight: 700; cursor: pointer; font-family: inherit; margin-top: 8px;
          transition: opacity 0.2s;
        }
        .sl-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .sl-btn:hover:not(:disabled) { opacity: 0.9; }
        .sl-error {
          background: #7f1d1d; color: #fca5a5; padding: 10px 14px;
          border-radius: 10px; font-size: 13px; text-align: center;
        }

        /* Install Banner */
        .sl-install-banner {
          width: 100%; max-width: 380px;
          background: linear-gradient(135deg, #1e293b, #0f172a);
          border: 1px solid #334155; border-radius: 20px;
          padding: 20px; display: flex; flex-direction: column;
          align-items: center; gap: 12px; text-align: center;
          animation: slSlideDown 0.4s ease-out;
        }
        @keyframes slSlideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        .sl-install-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #f59e0b, #ea580c);
          display: flex; align-items: center; justify-content: center;
          color: white;
        }
        .sl-install-text strong { display: block; color: #f8fafc; font-size: 15px; margin-bottom: 2px; }
        .sl-install-text span { color: #64748b; font-size: 12px; }
        .sl-install-btn {
          width: 100%; padding: 12px; border: none; border-radius: 12px;
          background: #22c55e; color: white; font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: inherit;
        }
        .sl-install-ios {
          background: #0f172a; border-radius: 12px; padding: 12px 16px;
          width: 100%; text-align: left;
        }
        .sl-install-ios p {
          color: #94a3b8; font-size: 13px; margin: 4px 0;
          display: flex; align-items: center; gap: 6px;
        }
        .sl-install-ios svg { color: #f59e0b; }
        .sl-install-dismiss {
          background: none; border: none; color: #475569; font-size: 12px;
          cursor: pointer; font-family: inherit; padding: 4px;
        }
      `}</style>
    </div>
  );
};

export default StaffLogin;
